// tslint:disable: member-ordering
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    startWith,
} from 'rxjs/operators';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AuthService } from '@services/security';
import { FormControl } from '@angular/forms';
import { of, tap } from 'rxjs';

import { LoaderService } from '@ui/loader';
import { ToastrService } from 'ngx-toastr';
import { UserService } from '@services/user';
import { TranslateService } from '@ngx-translate/core';
import { SidebarService } from '@ui/sidebar';
import { PatientsService } from '@services/patients';
import { format } from 'date-fns';
import { isEmpty, deburr } from 'lodash';
import { BackPageService } from '@services/back-page/back-page.service';

@UntilDestroy()
@Component({
    selector: 'new-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
    @Input() h1;
    @Input() back;
    @Input() isUsingBackpageService = false;
    @Input() show = false;
    @Input() diseaseShow = false;
    @Input() actions = false;
    @Input() debounce = 300;
    @Output() searchChanged = new EventEmitter<string>();
    @Input() profile = false;

    languages = [
        {
            value: 'fr',
            viewValue: 'Français',
            img: 'assets/image/flag-france.svg',
        },
        {
            value: 'en',
            viewValue: 'English',
            img: 'assets/image/flag-UK.svg',
        },
        {
            value: 'de',
            viewValue: 'Deutsch',
            img: 'assets/image/flag-germany.svg',
        }
    ];
    selected;
    lang;
    currentUser;

    @HostListener('window:resize', ['$event.target'])
    private windowResize(wnd: Window): void {
        this.resize(wnd.innerWidth);
    }

    private type;
    private width;

    public searchControl: FormControl;

    color;
    searchText;
    showSearchbar = false;
    patients;
    @Input() quickSelect = false;
    patient: any = {};
    typeNumber = false;

    constructor(
        private readonly router: Router,
        public authService: AuthService,
        private readonly translate: TranslateService,
        private loaderService: LoaderService,
        private toastr: ToastrService,
        private userService: UserService,
        private sidebarService: SidebarService,
        private patientsService: PatientsService,
        private change: ChangeDetectorRef,
        private backPageService: BackPageService,
    ) {}

    ngOnInit(): void {
        this.initUser();
        this.initSearchControl();
    }

    public search(): void {
        this.searchControl.setValue(this.searchControl.value);
    }

    public goBackPage() {
        this.backPageService.goBackPage();
    }

    public redirect(): void {
        this.router.navigate([this.back]);
    }

    public cleanSearch(): void {
        this.searchText = '';
        this.searchControl.setValue('');
        this.searchChanged.emit('');
        this.patients = [];
    }

    private initSearchControl(): void {
        this.searchControl = new FormControl('');
        const params = this.backPageService.getLastParamsRoute();
        if (params?.back) {
            this.searchControl.setValue(params?.q);
            this.searchText = true;
        }
        this.searchControl.valueChanges
            .pipe(
                startWith(''),
                distinctUntilChanged(),
                map((search: string) => {
                    return search.trim();
                }),
                filter((search: string) => {
                    return search.length > 0;
                }),
                debounceTime(this.debounce),
                tap((search: string) => {
                    this.searchChanged.emit(search);
                    this.searchText = true;
                    this.checkType(search);
                    this.searchPatientsDashboard(search);
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    private initHeader(user): void {
        this.type = user.type;
    }

    private resize(width): void {
        this.width = width;
    }

    private initUser(): void {
        this.authService.currentUser
            .pipe(
                tap((user) => {
                    this.initHeader(user);
                    this.currentUser = user;
                    this.lang = user.language;
                    this.selected = this.languages.find(
                        (lang) => lang.value === this.lang,
                    ) ?? this.languages[0];
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    changeLanguage(event): void {
        this.loaderService.enable();
        this.lang = event.value.value;
        const params = { language: this.lang };

        this.userService
            .updateCurrentUser(this.currentUser._id, params, 'language')
            .pipe(
                tap(() => {
                    this.translate.use(this.lang);
                    this.loaderService.disable();
                }),
                catchError((err) => {
                    this.toastr.error(this.translate.instant('error'));
                    this.loaderService.disable();
                    return of(err);
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    toggleSidebar(): void {
        this.sidebarService.toggle();
    }

    selectPatient($event) {
        this.patient = this.patients?.find(
            (item) =>
                `${this.normalizeNames(
                    item.lastName.toUpperCase(),
                )} ${this.normalizeNames(
                    this.capitalizeFirstLetter(item.firstName),
                )} - ${format(
                    item.birthDate,
                    'dd/MM/yyyy',
                )}${this.addNumberProperty(item, $event)}` ===
                $event.target.value,
        );

        if (!this.patient) return;
        this.router.navigate(['patient', this.patient._id]);
    }

    private searchPatientsDashboard(value) {
        if (!this.quickSelect) return;
        const selectedPatient = !isEmpty(this.patient)
            ? `${this.normalizeNames(
                  this.patient.lastName.toUpperCase(),
              )} ${this.normalizeNames(
                  this.capitalizeFirstLetter(this.patient.firstName),
              )} - ${format(
                  this.patient.birthDate,
                  'dd/MM/yyyy',
              )}${this.addNumberProperty(this.patient, value)}`
            : null;

        if (value !== selectedPatient && !value.includes('-')) {
            this.patientsService
                .searchPatientsDashboard(value)
                .subscribe((result) => {
                    this.patients = result.patients;
                    this.change.detectChanges();
                });
        }
    }

    checkType(value: string) {
        if (value.length > 2) {
            const asNumber = Number(value);
            this.typeNumber = Number.isSafeInteger(asNumber);
        }
    }

    addNumberProperty(item, event) {
        if (!this.typeNumber) return '';
        let numberResult = '';

        if (item.socialSecurityNumber && event.target.value.includes('nir')) {
            numberResult += ` - nir: ${item.socialSecurityNumber}`;
        }
        if (item.ins && event.target.value.includes('ins')) {
            numberResult += ` - ins: ${item.ins}`;
        }
        if (item?.ipp && event?.target?.value?.includes('ipp')) {
            numberResult += ` - ipp: ${item.ipp}`;
        }
        return numberResult;
    }

    normalizeNames(string: string) {
        return deburr(string);
    }

    capitalizeFirstLetter(str: string) {
        if (!str) return str;
        return str.charAt(0).toUpperCase() + str.slice(1);
    }
}
